import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../../../../shared/lib/supabase'
import { formatResponseValue, SECTION_LABELS } from '../../../../../shared/utils/assessmentDiff'
import { calculateScores } from '../../../../../shared/utils/scoring'
import { MATURITY_LEVELS } from '../../../../../shared/utils/maturityLevels'

// Map DB section names to display-friendly keys
const DB_SECTION_MAP = {
  risk_appetite: 'riskAppetite',
  business_scoping: 'businessScoping',
  technology_scoping: 'technologyScoping',
  maturity_assessment: 'maturityAssessment'
}

export default function DiscoveryResultsView({ client }) {
  const [changeLog, setChangeLog] = useState([])
  const [changeLogOpen, setChangeLogOpen] = useState(false)

  // Find latest completed assessment
  const latestAssessment = useMemo(() => {
    if (!client?.assessments?.length) return null
    return client.assessments
      .filter(a => a.status === 'completed')
      .sort((a, b) => new Date(b.completedAt || b.completed_at) - new Date(a.completedAt || a.completed_at))[0] || null
  }, [client?.assessments])

  // Compute scores
  const scores = useMemo(() => {
    if (!latestAssessment?.responses?.maturityAssessment) return null
    try { return calculateScores(latestAssessment.responses) } catch { return null }
  }, [latestAssessment?.responses])

  // Fetch change log
  useEffect(() => {
    if (!latestAssessment?.id) return
    supabase
      .from('assessment_change_log')
      .select('*')
      .eq('assessment_id', latestAssessment.id)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error('[DiscoveryResults] Change log fetch error:', error)
        setChangeLog(data || [])
        if (data?.length > 0) setChangeLogOpen(true)
      })
  }, [latestAssessment?.id])

  const currentLevel = scores ? MATURITY_LEVELS.find(l => l.level === (scores.overall?.maturityLevel?.level || 1)) : null
  const completedDate = latestAssessment?.completedAt || latestAssessment?.completed_at
    ? new Date(latestAssessment.completedAt || latestAssessment.completed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null

  // No completed assessment — show start page
  if (!latestAssessment || !scores) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Start Assessment</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Begin a security maturity assessment for {client?.name || 'this client'}. The assessment covers risk appetite, business context, technology scoping, and maturity across 14 security domains.
        </p>
        <Link
          to={`/clients/${client.id}/new-assessment`}
          className="btn-primary inline-flex items-center gap-2"
        >
          Begin Assessment
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    )
  }

  // Completed assessment — landing page
  return (
    <div className="space-y-5">
      {/* Assessment Summary Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Navy header */}
        <div className="p-5" style={{ background: 'linear-gradient(135deg, #012438, #213C4E)' }}>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#7E8F9C' }}>
                {latestAssessment.title || 'Security Assessment'}
              </p>
              <p className="text-lg font-bold text-white">
                Maturity Score: {scores.overall.score} / 100
              </p>
              {completedDate && (
                <p className="text-xs mt-1" style={{ color: '#BCC5CD' }}>Completed {completedDate}</p>
              )}
            </div>
            {currentLevel && (
              <div className="text-center px-4 py-2 rounded-xl" style={{ background: currentLevel.color + '20' }}>
                <p className="text-2xl font-extrabold leading-none" style={{ color: currentLevel.color }}>L{currentLevel.level}</p>
                <p className="text-xs mt-1" style={{ color: '#BCC5CD' }}>{currentLevel.label}</p>
              </div>
            )}
          </div>
        </div>

        {/* CMMI level bar */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1">
            {MATURITY_LEVELS.map(level => {
              const isActive = currentLevel?.level === level.level
              const isPast = currentLevel && level.level < currentLevel.level
              return (
                <div key={level.level} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="h-1.5 w-full rounded-full"
                    style={{ backgroundColor: isPast || isActive ? level.color : '#F1F5F9', opacity: isPast ? 0.4 : 1 }}
                  />
                  <p className="text-center" style={{ fontSize: '9px', fontWeight: isActive ? 700 : 400, color: isActive ? level.color : '#94A3B8' }}>
                    L{level.level}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-gray-700">
          <div className="p-4 text-center">
            <p className="text-xl font-bold text-gray-900 dark:text-white">{scores.overall.categoryCount || 14}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Domains Assessed</p>
          </div>
          <div className="p-4 text-center">
            <p className="text-xl font-bold text-gray-900 dark:text-white">{scores.riskAppetite?.averageAppetite || '-'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Risk Appetite ({scores.riskAppetite?.averageLabel || '-'})</p>
          </div>
          <div className="p-4 text-center">
            <p className="text-xl font-bold text-gray-900 dark:text-white">{scores.overall.maturityLevel?.label}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Maturity Level</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Link
          to={`/assessment/${latestAssessment.id}?mode=edit`}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 hover:border-primary-300 dark:hover:border-primary-700 transition-colors group"
        >
          <div className="flex items-center gap-3 mb-2">
            <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">Review & Edit Assessment</h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Review responses, update answers, and track changes in the audit log.
          </p>
        </Link>

        <Link
          to={`/clients/${client.id}/new-assessment`}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 hover:border-teal-300 dark:hover:border-teal-700 transition-colors group"
        >
          <div className="flex items-center gap-3 mb-2">
            <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">Begin Reassessment</h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Start a new assessment cycle. Previous results are preserved for trend analysis.
          </p>
        </Link>
      </div>

      {/* Change Log */}
      {changeLog.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setChangeLogOpen(!changeLogOpen)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Change Log</span>
              <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                {changeLog.length} {changeLog.length === 1 ? 'change' : 'changes'}
              </span>
            </div>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${changeLogOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {changeLogOpen && (
            <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
              {(() => {
                const sessions = []
                let currentSession = null
                changeLog.forEach(entry => {
                  const ts = new Date(entry.created_at).getTime()
                  if (!currentSession || Math.abs(ts - currentSession.ts) > 5000 || entry.reason !== currentSession.reason) {
                    currentSession = { ts, reason: entry.reason, user_email: entry.user_email, created_at: entry.created_at, previous_overall_score: entry.previous_overall_score, new_overall_score: entry.new_overall_score, entries: [] }
                    sessions.push(currentSession)
                  }
                  currentSession.entries.push(entry)
                })

                return sessions.map((session, si) => (
                  <div key={si} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-900 dark:text-white">{session.user_email}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {new Date(session.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                      {session.previous_overall_score != null && session.new_overall_score != null && session.previous_overall_score !== session.new_overall_score && (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          session.new_overall_score > session.previous_overall_score
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        }`}>
                          {session.previous_overall_score}% → {session.new_overall_score}%
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 italic">"{session.reason}"</p>
                    <div className="space-y-1.5">
                      {session.entries.map((entry, ei) => {
                        const sectionKey = DB_SECTION_MAP[entry.section] || entry.section
                        return (
                          <div key={ei} className="flex items-start gap-2 text-xs">
                            <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium">
                              {SECTION_LABELS[sectionKey] || entry.section}
                            </span>
                            <span className="text-gray-700 dark:text-gray-300 min-w-0">
                              {entry.question_label?.length > 60 ? entry.question_label.slice(0, 60) + '...' : entry.question_label}:
                            </span>
                            <span className="text-red-500 dark:text-red-400 line-through flex-shrink-0">
                              {formatResponseValue(sectionKey, entry.previous_value)}
                            </span>
                            <span className="text-gray-400 flex-shrink-0">→</span>
                            <span className="text-green-600 dark:text-green-400 font-medium flex-shrink-0">
                              {formatResponseValue(sectionKey, entry.new_value)}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
